import { Farm as FarmProto, Zone } from "protos/farm/farm";
import { Paper } from "@mui/material";
import { PointsSelectionList } from "./PointSelectionList";
import { Section } from "portal/components/map/navigation/Section";
import { Selection, setFocus, setSelection } from "portal/state/farmExplorer";
import { useDispatch } from "react-redux";
import { useFarmExplorer } from "portal/state/store";
import { useMuiColor } from "common/hooks/useMuiColor";
import { useTranslation } from "react-i18next";
import { ZoneSelection } from "./ZoneSelection";
import BoundaryIcon from "portal/images/icons/fence.svg?react";
import CloseIcon from "@mui/icons-material/Close";
import FieldIcon from "portal/images/icons/field.svg?react";
import HeadlandIcon from "common/images/icons/headland.svg?react";
import ObstacleIcon from "portal/images/icons/obstacle.svg?react";
import PathIcon from "portal/images/icons/conversion_path.svg?react";
import PivotIcon from "common/images/icons/irrigation.svg?react";
import PlaceIcon from "@mui/icons-material/Place";
import React, { FC, ReactNode, useState } from "react";
import ReadMoreIcon from "@mui/icons-material/ReadMore";

interface SelectionPanelProps {
  farm: FarmProto;
  /** selects a zone and fits the map to it */
  onGoToZone: (zone: Zone) => void;
}

interface ZoneButtonProps {
  zone: Zone;
  onGoToZone: (zone: Zone) => void;
}
const ZoneButton: FC<ZoneButtonProps> = ({ zone, onGoToZone }) => {
  const { selection, focus } = useFarmExplorer();
  const dispatch = useDispatch();
  const setSelectionState = (selection?: Selection): void => {
    dispatch(setSelection(selection));
  };

  const setFocusState = (selection?: Selection): void => {
    dispatch(setFocus(selection));
  };
  const isSelected =
    selection?.type === "zone" && selection.selection.id?.id === zone.id?.id;
  const isFocused =
    focus?.type === "zone" && focus.selection.id?.id === zone.id?.id;

  const [isHovered, setIsHovered] = useState<boolean>(false);

  const textColor = useMuiColor("textPrimary");

  return (
    <Section
      header={zone.name}
      isSelected={isSelected}
      isFocused={isFocused}
      isSubsection={true}
      startCollapsed
      key={`zone-${zone.id?.id}`}
      onMouseEnter={() => {
        setIsHovered(true);
        setFocusState({ type: "zone", selection: zone });
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setFocusState(undefined);
      }}
      action={
        isHovered || isSelected
          ? {
              icon: isSelected ? (
                <CloseIcon style={{ fill: textColor }} />
              ) : (
                <ReadMoreIcon style={{ fill: textColor }} />
              ),
              onClick: () =>
                isSelected ? setSelectionState(undefined) : onGoToZone(zone),
            }
          : undefined
      }
    >
      <ZoneSelection zone={zone} />
    </Section>
  );
};

export const SelectionPanel: FC<SelectionPanelProps> = ({
  farm,
  onGoToZone,
}: SelectionPanelProps) => {
  const { t } = useTranslation();

  const zoneSection = (
    title: string,
    filter: (z: Zone) => boolean,
    icon?: ReactNode
  ): ReactNode => (
    <Section
      header={
        <span className="flex gap-4">
          {icon}
          <span>{title}</span>
        </span>
      }
      startCollapsed
    >
      <div className="flex flex-col">
        {farm.zones
          .filter((z) => filter(z))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((zone) => (
            <ZoneButton key={zone.id?.id} zone={zone} onGoToZone={onGoToZone} />
          ))}
      </div>
    </Section>
  );

  const textColor = useMuiColor("textPrimary");

  return (
    <Paper className="overflow-y-auto w-full" style={{ maxHeight: "75vh" }}>
      {zoneSection(
        t("views.farms.zoneTypes.farmBoundary"),
        (z) => Boolean(z.contents?.farmBoundary),
        <BoundaryIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      {zoneSection(
        t("views.farms.zoneTypes.field_other"),
        (z) => Boolean(z.contents?.field),
        <FieldIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      {zoneSection(
        t("views.farms.zoneTypes.centerPivot_other"),
        (z) => Boolean(z.contents?.centerPivot),
        <PivotIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      {zoneSection(
        t("views.farms.zoneTypes.headland"),
        (z) => Boolean(z.contents?.headland),
        <HeadlandIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      {zoneSection(
        t("views.farms.zoneTypes.obstacle"),
        (z) => Boolean(z.contents?.obstacle),
        <ObstacleIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      {zoneSection(
        t("views.farms.zoneTypes.path"),
        (z) => Boolean(z.contents?.path),
        <PathIcon
          className="flex my-auto w-5 h-5"
          style={{ fill: textColor }}
        />
      )}
      <Section
        header={
          <span className="flex gap-4">
            <PlaceIcon
              className="flex my-auto w-5 h-5"
              style={{ fill: textColor }}
            />
            <span>{t("views.farms.selectionPanel.allPoints")}</span>
          </span>
        }
        startCollapsed
      >
        <div className="flex flex-col">
          <PointsSelectionList points={farm.pointDefs.map((p) => p.point)} />
        </div>
      </Section>
    </Paper>
  );
};
